import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SiteLayout from '@/components/feature/SiteLayout';
import VideoCard from '@/components/base/VideoCard';
import SectionHeader from '@/components/base/SectionHeader';
import { movies, searchVideos, tvShows } from '@/data/videos';
import { useSeo } from '@/hooks/useSeo';

const hotKeywords = ['悬疑', '科幻', '古装', '青春', '犯罪', '治愈'];

export default function SearchPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const keyword = (params.get('q') ?? '').trim();
  const [input, setInput] = useState(keyword);

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  const results = useMemo(() => searchVideos(keyword), [keyword]);

  const suggestions = useMemo(() => {
    const pool = [...movies, ...tvShows].sort((a, b) => b.rating - a.rating);
    return pool.slice(0, 6);
  }, []);

  useSeo({
    title: keyword ? `“${keyword}” 搜索结果 - 麻豆视频` : '影视搜索 - 麻豆视频',
    description: keyword
      ? `麻豆视频搜索结果页，展示与“${keyword}”相关的电影与电视剧演示条目，可按标题、类型与主演匹配。`
      : '麻豆视频全站搜索，支持按片名、类型、主演关键词检索电影与电视剧演示内容。',
    keywords: '麻豆视频,影视搜索,电影搜索,电视剧搜索',
  });

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const value = input.trim();
    if (!value) return;
    navigate(`/search?q=${encodeURIComponent(value)}`);
  };

  return (
    <SiteLayout>
      <div className="animate-fade-in mx-auto w-full max-w-[1280px] px-4 pt-5 md:px-8 md:pt-6">
        <nav aria-label="面包屑" className="flex flex-wrap items-center gap-1.5 text-xs text-foreground-500">
          <Link to="/" className="cursor-pointer transition-colors hover:text-primary-600">
            首页
          </Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400" />
          <span className="text-foreground-800">搜索结果</span>
        </nav>

        <section className="relative mt-3 overflow-hidden rounded-lg bg-primary-950 p-6 text-background-50 md:p-9">
          <i className="ri-search-eye-line pointer-events-none absolute -right-3 -top-4 text-[150px] text-primary-800/40" />
          <div className="relative max-w-2xl">
            <span className="mb-3 inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2 py-1 font-label text-[11px] font-bold text-primary-950">
              <i className="ri-search-2-line text-sm" />
              全站搜索
            </span>
            <h1 className="font-heading text-2xl font-extrabold tracking-tight md:text-[32px]">
              搜索结果
            </h1>
            <p className="mt-2 text-xs leading-relaxed text-primary-200 md:text-sm">
              支持按片名、类型、主演与简介关键词匹配。以下结果均为站内演示数据，用于展示搜索交互。
            </p>

            <form onSubmit={handleSubmit} role="search" className="mt-5">
              <div className="flex flex-col gap-2 sm:flex-row">
                <div className="relative flex-1">
                  <i className="ri-search-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-primary-300" />
                  <input
                    type="search"
                    name="q"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="输入片名、类型或演员关键词"
                    aria-label="搜索影视内容"
                    className="h-11 w-full rounded-md border border-primary-800 bg-primary-900/80 pl-10 pr-3 text-sm text-background-50 placeholder:text-primary-300 focus:border-accent-500 focus:outline-none"
                  />
                </div>
                <button
                  type="submit"
                  className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-6 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                >
                  搜索
                </button>
              </div>
            </form>

            <div className="mt-4 flex flex-wrap items-center gap-1.5">
              <span className="mr-1 text-[11px] text-primary-300">热门搜索</span>
              {hotKeywords.map((item) => (
                <Link
                  key={item}
                  to={`/search?q=${encodeURIComponent(item)}`}
                  className="cursor-pointer rounded-full border border-primary-700 px-2.5 py-1 text-[11px] text-primary-100 transition-colors hover:border-accent-500 hover:text-accent-300"
                >
                  {item}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {keyword === '' ? (
          <section className="mt-8">
            <SectionHeader
              eyebrow="Try searching"
              title="输入关键词开始查找"
              subtitle="例如输入“悬疑”“科幻”，或直接搜索片名与演员名字。"
            />
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-14 text-center">
              <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                <i className="ri-search-line text-2xl" />
              </span>
              <p className="mt-4 text-sm font-medium text-foreground-800">还没有输入搜索关键词</p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                在上方搜索框输入内容，即可查看匹配的电影与电视剧演示结果。
              </p>
            </div>

            <div className="mt-8">
              <SectionHeader eyebrow="Top rated" title="高分推荐" />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
                {suggestions.map((video) => (
                  <VideoCard key={`${video.type}-${video.id}`} video={video} />
                ))}
              </div>
            </div>
          </section>
        ) : (
          <section className="mt-6">
            <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-50 px-4 py-3">
              <p className="text-sm text-foreground-700">
                关键词 <span className="font-semibold text-primary-700">“{keyword}”</span>
                <span className="ml-1 text-foreground-500">共匹配 {results.length} 条演示结果</span>
              </p>
              <span className="flex items-center gap-1.5 rounded-md bg-accent-100 px-2.5 py-1 text-[11px] text-accent-800">
                <i className="ri-information-line text-xs" />
                演示搜索结果
              </span>
            </div>

            {results.length > 0 ? (
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4 lg:grid-cols-6">
                {results.map((video) => (
                  <VideoCard key={`${video.type}-${video.id}`} video={video} />
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-50 px-6 py-16 text-center">
                <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-100 text-foreground-400">
                  <i className="ri-search-line text-2xl" />
                </span>
                <h2 className="mt-4 font-heading text-base font-semibold text-foreground-900">
                  未找到相关内容
                </h2>
                <p className="mt-2 max-w-sm text-xs leading-relaxed text-foreground-500">
                  “{keyword}” 暂无匹配的演示条目，换个关键词试试，或直接浏览电影与电视剧分类。
                </p>
                <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
                  <Link
                    to="/category/movie"
                    className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-xs font-semibold text-primary-950 transition-colors hover:bg-accent-400"
                  >
                    浏览电影
                  </Link>
                  <Link
                    to="/category/tv"
                    className="cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-xs font-medium text-foreground-700 transition-colors hover:border-accent-300 hover:text-primary-600"
                  >
                    浏览电视剧
                  </Link>
                </div>
              </div>
            )}

            <p className="mt-6 rounded-lg bg-background-200/60 px-4 py-3 text-[11px] leading-relaxed text-foreground-500">
              搜索基于站内演示数据在浏览器本地完成匹配，不会向外部服务发送查询内容。
            </p>
          </section>
        )}
      </div>
    </SiteLayout>
  );
}